<template>
  <div class="refund-container">
    <!-- 统计卡片 -->
    <el-row :gutter="20" class="statistics-row">
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card pending">
          <div class="stat-content">
            <div class="stat-label">待审核</div>
            <div class="stat-value">{{ statistics.pending }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card refunding">
          <div class="stat-content">
            <div class="stat-label">退款中</div>
            <div class="stat-value">{{ statistics.refunding }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card success">
          <div class="stat-content">
            <div class="stat-label">退款成功</div>
            <div class="stat-value">{{ statistics.success }}</div>
          </div>
        </el-card>
      </el-col>
      <el-col :span="6">
        <el-card shadow="hover" class="stat-card failed">
          <div class="stat-content">
            <div class="stat-label">退款失败</div>
            <div class="stat-value">{{ statistics.failed }}</div>
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- 筛选条件 -->
    <el-card class="filter-card" shadow="never">
      <el-form :inline="true" :model="filters" class="filter-form">
        <!-- 退款状态 -->
        <el-form-item label="退款状态">
          <el-select
            v-model="filters.refund_status"
            placeholder="请选择"
            clearable
            style="width: 150px"
          >
            <el-option label="全部" :value="-1" />
            <el-option label="待审核" :value="0" />
            <el-option label="审批通过" :value="1" />
            <el-option label="审批拒绝" :value="2" />
            <el-option label="退款中" :value="3" />
            <el-option label="退款成功" :value="4" />
            <el-option label="退款失败" :value="5" />
          </el-select>
        </el-form-item>

        <!-- 操作按钮 -->
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleFilter">
            查询
          </el-button>
          <el-button icon="el-icon-refresh-left" @click="handleReset">
            重置
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 卡片式列表 -->
    <div v-loading="listLoading" class="refund-list">
      <el-card
        v-for="(refund, index) in refundList"
        :key="refund.refund_no"
        class="refund-card"
        :class="getRefundCardClass(refund, index)"
        shadow="hover"
      >
        <!-- 第一行：单号和套餐信息 -->
        <div class="refund-row row-main">
          <div class="refund-item">
            <span class="label">退款单号:</span>
            <el-link type="primary" @click="handleDetail(refund)">
              {{ refund.refund_no }}
            </el-link>
          </div>
          <div class="refund-item">
            <span class="label">订单号:</span>
            <el-link @click="handleOrderDetail(refund)">
              {{ refund.order_no }}
            </el-link>
          </div>
          <div class="refund-item">
            <span class="label">套餐名称:</span>
            <span class="value" :title="refund.order && refund.order.package_name">
              {{ refund.order && refund.order.package_name || '-' }}
            </span>
          </div>
          <div class="refund-item">
            <span class="label">退款状态:</span>
            <el-tag :type="getRefundStatusTagType(refund.refund_status)" size="small">
              {{ getRefundStatusText(refund.refund_status) }}
            </el-tag>
          </div>
        </div>

        <!-- 第二行：金额和商户信息 -->
        <div class="refund-row">
          <div class="refund-item">
            <span class="label">订单金额:</span>
            <span class="value">¥{{ formatMoney(refund.order_amount) }}</span>
          </div>
          <div class="refund-item">
            <span class="label">退款金额:</span>
            <span class="value refund-price">¥{{ formatMoney(refund.refund_amount) }}</span>
          </div>
          <div class="refund-item">
            <span class="label">收款账户:</span>
            <span class="value">{{ refund.order && refund.order.merchant_name || '-' }}</span>
          </div>
          <div class="refund-item">
            <span class="label">店铺名称:</span>
            <span class="value">{{ refund.order && refund.shop_name || '-' }}</span>
          </div>
        </div>

        <!-- 第三行：退款原因和时间 -->
        <div class="refund-row">
          <div class="refund-item reason-item">
            <span class="label">退款原因:</span>
            <span class="value" :title="refund.refund_reason">
              {{ refund.refund_reason || '-' }}
            </span>
          </div>
          <div class="refund-item">
            <span class="label">申请时间:</span>
            <span class="value">{{ formatDate(refund.created_at) }}</span>
          </div>
          <div class="refund-item actions">
            <el-dropdown @command="handleCommand($event, refund)" trigger="click">
              <el-button type="primary" size="small">
                操作 <i class="el-icon-arrow-down el-icon--right"></i>
              </el-button>
              <el-dropdown-menu slot="dropdown">
                <!-- 审批退款（待审核状态时显示） -->
                <el-dropdown-item
                  v-if="refund.refund_status === 0"
                  command="approve"
                  icon="el-icon-s-check"
                >
                  审批退款
                </el-dropdown-item>

                <!-- 查看订单（始终显示） -->
                <el-dropdown-item
                  command="viewOrder"
                  icon="el-icon-tickets"
                >
                  查看订单
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </div>
        </div>
      </el-card>

      <!-- 空状态 -->
      <el-empty v-if="!listLoading && refundList.length === 0" description="暂无退款数据"></el-empty>
    </div>

    <!-- 分页 -->
    <div class="pagination-container">
      <el-pagination
        :current-page="listQuery.page"
        :page-sizes="[10, 20, 50, 100]"
        :page-size="listQuery.page_size"
        :total="total"
        background
        layout="total, sizes, prev, pager, next, jumper"
        @size-change="handleSizeChange"
        @current-change="handleCurrentChange"
      />
    </div>

    <!-- 审批弹窗 -->
    <refund-approve-dialog
      ref="approveDialog"
      @success="fetchRefundList"
    />
  </div>
</template>

<script>
import { getRefundList } from '@/api/iccid-refund'
import RefundApproveDialog from './components/RefundApproveDialog'
import {
  getRefundStatusText,
  getRefundStatusTagType,
  formatMoney,
  formatDate
} from '@/utils/iccid-formatter'

export default {
  name: 'IccidRefundList',
  components: {
    RefundApproveDialog
  },
  data() {
    return {
      refundList: [],
      total: 0,
      listLoading: false,
      listQuery: {
        page: 1,
        page_size: 20
      },
      filters: {
        refund_status: -1
      },
      statistics: {
        pending: 0,       // 待审核
        refunding: 0,     // 退款中
        success: 0,       // 退款成功
        failed: 0         // 退款失败
      }
    }
  },
  created() {
    this.fetchRefundList()
    this.fetchStatistics()
  },
  methods: {
    // 格式化函数
    formatMoney,
    formatDate,
    getRefundStatusText,
    getRefundStatusTagType,

    // 获取退款列表
    async fetchRefundList() {
      this.listLoading = true
      try {
        const params = {
          ...this.listQuery,
          ...this.filters
        }

        const { data } = await getRefundList(params)
        this.refundList = data.list || []
        this.total = data.total || 0
      } catch (error) {
        this.$message.error('获取退款列表失败')
      } finally {
        this.listLoading = false
      }
    },

    // 获取统计数据
    async fetchStatistics() {
      try {
        // 获取各状态的数量
        const statusList = [0, 3, 4, 5]  // 待审核、退款中、退款成功、退款失败
        const promises = statusList.map(refund_status =>
          getRefundList({ page: 1, page_size: 1, refund_status })
        )
        const results = await Promise.all(promises)

        this.statistics = {
          pending: results[0].data.total || 0,    // 待审核 (0)
          refunding: results[1].data.total || 0,  // 退款中 (3)
          success: results[2].data.total || 0,    // 退款成功 (4)
          failed: results[3].data.total || 0      // 退款失败 (5)
        }
      } catch (error) {
        console.error('获取统计数据失败', error)
      }
    },

    // 查询
    handleFilter() {
      this.listQuery.page = 1
      this.fetchRefundList()
    },

    // 重置
    handleReset() {
      this.filters = {
        refund_status: -1
      }
      this.listQuery.page = 1
      this.fetchRefundList()
    },

    // 分页
    handleSizeChange(val) {
      this.listQuery.page_size = val
      this.fetchRefundList()
    },
    handleCurrentChange(val) {
      this.listQuery.page = val
      this.fetchRefundList()
    },

    // 处理下拉菜单命令
    handleCommand(command, row) {
      switch (command) {
        case 'approve':
          this.handleApprove(row)
          break
        case 'viewOrder':
          this.handleOrderDetail(row)
          break
      }
    },

    // 审批
    handleApprove(row) {
      this.$refs.approveDialog.open(row)
    },

    // 查看详情
    handleDetail(row) {
      // 可以实现详情页
      console.log('查看详情:', row)
    },

    // 查看订单详情
    handleOrderDetail(row) {
      this.$router.push({
        name: 'IccidOrderList',
        query: { order_no: row.order_no }
      })
    },

    // 获取退款卡片样式类
    getRefundCardClass(refund, index) {
      const classes = []

      // 根据退款状态添加边框颜色
      // 0:待审核 1:审批通过 2:审批拒绝 3:退款中 4:退款成功 5:退款失败
      switch (refund.refund_status) {
        case 0:
          // 待审核 - 橙色
          classes.push('card-pending')
          break
        case 1:
          // 审批通过 - 蓝色
          classes.push('card-approved')
          break
        case 2:
          // 审批拒绝 - 红色
          classes.push('card-rejected')
          break
        case 3:
          // 退款中 - 浅蓝色
          classes.push('card-refunding')
          break
        case 4:
          // 退款成功 - 绿色
          classes.push('card-success')
          break
        case 5:
          // 退款失败 - 红色
          classes.push('card-failed')
          break
      }

      return classes.join(' ')
    }
  }
}
</script>

<style lang="scss" scoped>
.refund-container {
  padding: 20px;
}

.statistics-row {
  margin-bottom: 20px;
}

.stat-card {
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    transform: translateY(-5px);
  }
}

.stat-content {
  text-align: center;
  padding: 10px 0;
}

.stat-label {
  font-size: 14px;
  color: #909399;
  margin-bottom: 10px;
}

.stat-value {
  font-size: 32px;
  font-weight: bold;
}

.stat-card.pending .stat-value {
  color: #E6A23C;
}

.stat-card.refunding .stat-value {
  color: #409EFF;
}

.stat-card.success .stat-value {
  color: #67C23A;
}

.stat-card.failed .stat-value {
  color: #F56C6C;
}

.filter-card {
  margin-bottom: 20px;
}

.filter-form {
  margin-bottom: -18px;
}

.refund-list {
  min-height: 400px;
}

.refund-card {
  margin-bottom: 16px;
  transition: all 0.3s;
  border-left: 4px solid transparent;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  }

  &:last-child {
    margin-bottom: 0;
  }

  // 待审核 - 橙色
  &.card-pending {
    border-left-color: #E6A23C;
    background: linear-gradient(to right, rgba(230, 162, 60, 0.02), transparent);
  }

  // 审批通过 - 蓝色
  &.card-approved {
    border-left-color: #409EFF;
    background: linear-gradient(to right, rgba(64, 158, 255, 0.02), transparent);
  }

  // 审批拒绝 - 红色
  &.card-rejected {
    border-left-color: #F56C6C;
    background: linear-gradient(to right, rgba(245, 108, 108, 0.02), transparent);
  }

  // 退款中 - 浅蓝色
  &.card-refunding {
    border-left-color: #409EFF;
    background: linear-gradient(to right, rgba(64, 158, 255, 0.03), transparent);
  }

  // 退款成功 - 绿色
  &.card-success {
    border-left-color: #67C23A;
    background: linear-gradient(to right, rgba(103, 194, 58, 0.02), transparent);
  }

  // 退款失败 - 红色
  &.card-failed {
    border-left-color: #F56C6C;
    background: linear-gradient(to right, rgba(245, 108, 108, 0.02), transparent);
  }
}

.refund-row {
  display: flex;
  align-items: center;
  padding: 8px 0;
  border-bottom: 1px solid #f0f0f0;

  &:last-child {
    border-bottom: none;
  }

  &.row-main {
    padding-top: 0;

    .value {
      font-weight: 500;
    }
  }
}

.refund-item {
  flex: 1;
  display: flex;
  align-items: center;
  min-width: 0;
  padding: 0 12px;

  &:first-child {
    padding-left: 0;
  }

  &:last-child {
    padding-right: 0;
  }

  &.reason-item {
    flex: 2;

    .value {
      max-width: 100%;
    }
  }

  &.actions {
    flex: 0 0 auto;
    justify-content: flex-end;
  }

  .label {
    color: #909399;
    font-size: 13px;
    margin-right: 8px;
    white-space: nowrap;
  }

  .value {
    color: #303133;
    font-size: 13px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;

    &.refund-price {
      color: #F56C6C;
      font-weight: 600;
      font-size: 14px;
    }
  }

  ::v-deep .el-link {
    font-size: 13px;
    font-weight: 500;
  }

  ::v-deep .el-tag {
    margin-left: 0;
  }
}

.pagination-container {
  margin-top: 20px;
  text-align: right;
}
</style>
